import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api/axios';

import type {
  PamActionCostEstimateResponse,
  PamAssignmentPreviewResponse,
  PamAssignmentScopeKind,
  PamBoardReportStart,
  PamContactsResponse,
  PamAutofillBookmarkletResponse,
  PamContactsSaveRequest,
  PamCostedActionsListResponse,
  PamDashboardResponse,
  PamDiscoverableGroup,
  PamDiscoverableGroupsResponse,
  PamDomainOwnersUpdate,
  PamGroupSaqsResponse,
  PamEvidenceDocumentsResponse,
  PamEvidenceSite,
  PamEvidenceSitesResponse,
  PamEvidenceStructureDomain,
  PamEvidenceStructureResponse,
  PamFmMaturityResponse,
  PamMembersOverviewResponse,
  PamQuestionAssigneesUpdate,
  PamQuestionDocumentEvidenceBulkCreate,
  PamQuestionsOverviewResponse,
  PamSubmissionsListResponse,
  PamIterationsListResponse,
  PamSubmissionCreateRequest,
  PamSubmissionCreateResponse,
  PamSubmissionStatusResponse,
  PamTenantMember,
  PamSaqAssigneesUpdate,
  PamSaqComment,
  PamSaqCommentCreate,
  PamSaqCommentUpdate,
  PamSaqActivityResponse,
  PamSaqCompleteUpdate,
  PamSaqDeadlineUpdate,
  PamSaqNoteUpdate,
  PamSaqGroupUpdate,
  PamSaqOverviewResponse
} from './types';

const BASE_PATH = '/pam';

export async function getPamSubmissions(): Promise<PamSubmissionsListResponse> {
  const response = await apiGet<PamSubmissionsListResponse>(`${BASE_PATH}/submissions`);
  return response.data;
}

export async function getPamIterations(): Promise<PamIterationsListResponse> {
  const response = await apiGet<PamIterationsListResponse>(`${BASE_PATH}/iterations`);
  return response.data;
}

export async function submitPamSubmission(submissionId: string): Promise<PamSubmissionStatusResponse> {
  const response = await apiPost<PamSubmissionStatusResponse>(`${BASE_PATH}/submissions/${submissionId}/submit`);
  return response.data;
}

export async function unlockPamSubmission(submissionId: string): Promise<PamSubmissionStatusResponse> {
  const response = await apiPost<PamSubmissionStatusResponse>(`${BASE_PATH}/submissions/${submissionId}/unlock`);
  return response.data;
}

export async function createPamSubmission(payload: PamSubmissionCreateRequest): Promise<PamSubmissionCreateResponse> {
  const response = await apiPost<PamSubmissionCreateResponse>(`${BASE_PATH}/submissions`, payload);
  return response.data;
}

export async function getPamDashboard(submissionId: string): Promise<PamDashboardResponse> {
  const response = await apiGet(`${BASE_PATH}/submissions/${submissionId}/dashboard`);
  return response.data;
}

/** Open a chat for the submission's board report and start generating it. */
export async function startPamBoardReport(submissionId: string): Promise<PamBoardReportStart> {
  const response = await apiPost<PamBoardReportStart>(
    `${BASE_PATH}/submissions/${submissionId}/board-report`,
    {},
    { meta: { globalError: true, context: 'pam-board-report-start' } }
  );
  return response.data;
}

export async function getPamAutofillBookmarklet(submissionId: string): Promise<PamAutofillBookmarkletResponse> {
  const response = await apiGet<PamAutofillBookmarkletResponse>(
    `${BASE_PATH}/submissions/${submissionId}/autofill-bookmarklet`,
    { meta: { globalError: true, context: 'pam-autofill-bookmarklet' } }
  );
  return response.data;
}

export async function getPamCostedActions(submissionId: string): Promise<PamCostedActionsListResponse> {
  const response = await apiGet<PamCostedActionsListResponse>(
    `${BASE_PATH}/submissions/${submissionId}/costed-actions`
  );
  return response.data;
}

export async function getPamContacts(submissionId: string): Promise<PamContactsResponse> {
  const response = await apiGet<PamContactsResponse>(`${BASE_PATH}/submissions/${submissionId}/contacts`);
  return response.data;
}

export async function savePamContacts(
  submissionId: string,
  payload: PamContactsSaveRequest
): Promise<PamContactsResponse> {
  const response = await apiPut<PamContactsResponse>(`${BASE_PATH}/submissions/${submissionId}/contacts`, payload);
  return response.data;
}

// Runs the LLM synchronously and returns a capital/revenue cost estimate for an
// existing PAM costed action. Nothing is persisted; the caller applies it by
// updating the action (Keep) or discards it (Reject). Errors are surfaced
// locally (no global toast): 404 = unavailable, 503 = AI failure (retryable).
export async function estimateCostedActionCost(actionId: string): Promise<PamActionCostEstimateResponse> {
  const response = await apiPost<PamActionCostEstimateResponse>(
    `${BASE_PATH}/costed-actions/${actionId}/cost-estimate`,
    {},
    { meta: { globalError: false, context: 'pam-costed-action-cost-estimate' } }
  );
  return response.data;
}

export async function getPamQuestionsOverview(
  submissionId: string,
  options?: { includeInaccessibleSaqs?: boolean }
): Promise<PamQuestionsOverviewResponse> {
  const response = await apiGet(`${BASE_PATH}/submissions/${submissionId}/questions-overview`, {
    params: options?.includeInaccessibleSaqs ? { include_inaccessible_saqs: true } : undefined
  });
  return response.data;
}

export async function getPamFmMaturity(submissionId: string): Promise<PamFmMaturityResponse> {
  const response = await apiGet(`${BASE_PATH}/submissions/${submissionId}/fm-maturity`);
  return response.data;
}

export async function getPamMembersOverview(submissionId: string): Promise<PamMembersOverviewResponse> {
  const response = await apiGet(`${BASE_PATH}/submissions/${submissionId}/members-overview`);
  return response.data;
}

export async function getPamTenantMembers(activeOnly = false): Promise<PamTenantMember[]> {
  const response = await apiGet<PamTenantMember[]>(`/tenant-management/members`, {
    params: { active_only: activeOnly },
    meta: { globalError: false, context: 'pam-tenant-members' }
  });
  return response.data;
}

export async function getPamSaqOverview(saqId: string): Promise<PamSaqOverviewResponse> {
  const response = await apiGet(`${BASE_PATH}/saqs/${saqId}`);
  return response.data;
}

// The four `update*Assignees` / owners / group helpers below are thin,
// scope-flavoured wrappers over the unified replace-at-scope endpoints
// (`PUT .../assignments/users` and `.../assignments/group`); the role is implied
// by which scope id is sent.
export async function updateSaqAssignees(
  submissionId: string,
  saqId: string,
  payload: PamSaqAssigneesUpdate
): Promise<void> {
  await apiPut(`${BASE_PATH}/submissions/${submissionId}/assignments/users`, {
    saq_id: saqId,
    user_ids: payload.user_ids ?? [],
    emails: payload.emails ?? []
  });
}

export async function updateSaqGroup(submissionId: string, saqId: string, payload: PamSaqGroupUpdate): Promise<void> {
  await apiPut(`${BASE_PATH}/submissions/${submissionId}/assignments/group`, {
    saq_id: saqId,
    team_group_id: payload.group_id
  });
}

export async function updateSaqDeadline(saqId: string, payload: PamSaqDeadlineUpdate): Promise<void> {
  await apiPatch(`${BASE_PATH}/saqs/${saqId}/deadline`, payload);
}

export async function updateSaqNote(saqId: string, payload: PamSaqNoteUpdate): Promise<void> {
  await apiPatch(`${BASE_PATH}/saqs/${saqId}/note`, payload);
}

export async function updateSaqComplete(saqId: string, payload: PamSaqCompleteUpdate): Promise<void> {
  await apiPatch(`${BASE_PATH}/saqs/${saqId}/complete`, payload);
}

export async function postSaqComment(saqId: string, payload: PamSaqCommentCreate): Promise<PamSaqComment> {
  const response = await apiPost<PamSaqComment>(`${BASE_PATH}/saqs/${saqId}/comments`, payload);
  return response.data;
}

export async function editSaqComment(
  saqId: string,
  commentId: string,
  payload: PamSaqCommentUpdate
): Promise<PamSaqComment> {
  const response = await apiPatch<PamSaqComment>(`${BASE_PATH}/saqs/${saqId}/comments/${commentId}`, payload);
  return response.data;
}

export async function removeSaqComment(saqId: string, commentId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}/saqs/${saqId}/comments/${commentId}`);
}

export async function markAllQuestionsAsNa(saqId: string): Promise<void> {
  await apiPost(`${BASE_PATH}/saqs/${saqId}/questions/mark-na`);
}

export async function updateQuestionAssignees(
  submissionId: string,
  _saqId: string,
  questionId: string,
  payload: PamQuestionAssigneesUpdate,
  // When true, replaces the GuestQuestion (limited) assignees rather than the
  // normal Question assignees — they're separate sets on the same question.
  guest = false
): Promise<void> {
  await apiPut(`${BASE_PATH}/submissions/${submissionId}/assignments/users`, {
    question_id: questionId,
    user_ids: payload.user_ids ?? [],
    emails: payload.emails ?? [],
    guest
  });
}

/** Link one or more existing hub documents as evidence on a question. */
export async function linkDocumentsAsEvidence(
  questionId: string,
  payload: PamQuestionDocumentEvidenceBulkCreate
): Promise<void> {
  await apiPost(`${BASE_PATH}/questions/${questionId}/evidence/link`, payload);
}

/**
 * Fan-out preview for a prospective assignment: how many questions / evidence
 * items / costed actions fall under `scope` over the given tree `ids`. Read-only
 * — called before committing so the UI can warn when access fans out. `ids` is
 * serialized as repeated `ids=` params (no brackets) to match the backend's
 * list query param.
 */
export async function getPamAssignmentPreview(
  submissionId: string,
  scope: PamAssignmentScopeKind,
  ids: string[]
): Promise<PamAssignmentPreviewResponse> {
  const response = await apiGet<PamAssignmentPreviewResponse>(
    `${BASE_PATH}/submissions/${submissionId}/assignments/preview`,
    {
      params: { scope, ids },
      paramsSerializer: { indexes: null },
      meta: { globalError: false, context: 'pam-assignment-preview' }
    }
  );
  return response.data;
}

/** Replace the PAM leads on a submission. Only existing PAM leads may call this. */
export async function replaceSubmissionLeads(submissionId: string, payload: { emails: string[] }): Promise<void> {
  await apiPut(`${BASE_PATH}/submissions/${submissionId}/assignments/leads`, {
    emails: payload.emails
  });
}

/** Replace the full set of owners on a domain (admin-only; users only). */
export async function updateDomainOwners(
  submissionId: string,
  submissionDomainId: string,
  payload: PamDomainOwnersUpdate
): Promise<void> {
  await apiPut(`${BASE_PATH}/submissions/${submissionId}/assignments/users`, {
    domain_id: submissionDomainId,
    user_ids: payload.user_ids ?? [],
    emails: payload.emails ?? []
  });
}

/**
 * Discoverable team groups in the requester's tenant — the SAQ-level group
 * picker source. 404s gracefully for non-standard members (handled by the
 * hook's `enabled` gate).
 */
export async function getPamDiscoverableGroups(): Promise<PamDiscoverableGroup[]> {
  const response = await apiGet<PamDiscoverableGroupsResponse>('/team-groups/', {
    meta: { globalError: false, context: 'pam-discoverable-groups' }
  });
  return response.data.groups;
}

/**
 * SAQs (with questions) assigned to a team group, for the group's PAM tab.
 * Member-only on the backend — 404s for non-members, so `globalError: false`
 * keeps that expected case from popping a global toast (the caller only enables
 * the query for members anyway).
 */
export async function getPamGroupSaqs(teamGroupId: string): Promise<PamGroupSaqsResponse> {
  const response = await apiGet<PamGroupSaqsResponse>(`${BASE_PATH}/team-groups/${teamGroupId}/saqs`, {
    meta: { globalError: false, context: 'pam-group-saqs' }
  });
  return response.data;
}

export async function getPamEvidenceStructure(
  submissionId: string,
  siteId?: string | null,
  search?: string
): Promise<PamEvidenceStructureDomain[]> {
  const q = search?.trim();
  const response = await apiGet<PamEvidenceStructureResponse>(`/pam/submissions/${submissionId}/evidence-structure`, {
    params: {
      ...(siteId ? { site_id: siteId } : {}),
      ...(q ? { search: q } : {})
    },
    meta: { globalError: false, context: 'pam-evidence-structure' }
  });
  return response.data.domains;
}

/** Matches ``PamOtherEvidenceRequest.question_ids`` ``max_length``. */
const OTHER_EVIDENCE_QUESTION_ID_LIMIT = 500;

type PamOtherEvidenceNote = {
  id: string;
  text: string | null;
  contact_name: string | null;
  contact_details: string | null;
  external_link: string | null;
  question_ref: string;
};

export async function getPamOtherEvidence(
  submissionId: string,
  questionIds: string[]
): Promise<PamOtherEvidenceNote[]> {
  if (questionIds.length === 0) return [];

  const notes: PamOtherEvidenceNote[] = [];
  for (let start = 0; start < questionIds.length; start += OTHER_EVIDENCE_QUESTION_ID_LIMIT) {
    const chunk = questionIds.slice(start, start + OTHER_EVIDENCE_QUESTION_ID_LIMIT);
    const response = await apiPost<{ notes: PamOtherEvidenceNote[] }>(
      `/pam/submissions/${submissionId}/other-evidence`,
      { question_ids: chunk },
      {
        meta: { globalError: false, context: 'pam-other-evidence' }
      }
    );
    notes.push(...response.data.notes);
  }
  return notes;
}

export async function getPamEvidenceDocuments(
  submissionId: string,
  siteId?: string | null,
  page = 1,
  pageSize = 12,
  questionId?: string | null,
  search?: string
): Promise<PamEvidenceDocumentsResponse> {
  const q = search?.trim();
  const response = await apiGet<PamEvidenceDocumentsResponse>(`/pam/submissions/${submissionId}/documents`, {
    params: {
      ...(siteId ? { site_id: siteId } : {}),
      ...(questionId ? { question_id: questionId } : {}),
      ...(q ? { search: q } : {}),
      page,
      page_size: pageSize
    },
    meta: { globalError: false, context: 'pam-evidence-documents' }
  });
  return response.data;
}

export async function getSaqActivity(
  saqId: string,
  { limit = 25, cursor }: { limit?: number; cursor?: string } = {}
): Promise<PamSaqActivityResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const response = await apiGet<PamSaqActivityResponse>(`${BASE_PATH}/saqs/${saqId}/activity?${params}`);
  return response.data;
}

export async function getPamEvidenceSites(submissionId: string): Promise<PamEvidenceSite[]> {
  const response = await apiGet<PamEvidenceSitesResponse>(`/pam/submissions/${submissionId}/evidence-sites`, {
    meta: { globalError: false, context: 'pam-evidence-sites' }
  });
  return response.data.sites;
}
